import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Book } from '../types';
import {
  getGraphQLClient,
  GET_BOOKS,
  GET_DRAFT_BOOKS,
  DELETE_BOOK,
  PUBLISH_BOOK,
} from '../lib/graphql';
import { Button, Spinner } from '../components/common';
import TranslationsTab from '../components/translation/TranslationsTab';
import CreateTranslationModal from '../components/translation/CreateTranslationModal';

export default function BooksPage() {
  const { t } = useTranslation();
  const [books, setBooks] = useState<Book[]>([]);
  const [draftBooksCount, setDraftBooksCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Books vs. Translations are two tabs of one "My Books" home. Tab is in the
  // URL (?tab=translations) so it deep-links and survives refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'translations' ? 'translations' : 'books';
  const setTab = (next: 'books' | 'translations') =>
    setSearchParams(next === 'translations' ? { tab: 'translations' } : {});
  const [showCreateTranslation, setShowCreateTranslation] = useState(false);

  const loadBooks = async () => {
    try {
      const client = getGraphQLClient();
      const [booksData, draftsData] = await Promise.all([
        client.request<{ books: Book[] }>(GET_BOOKS),
        client.request<{ draftBooks: Book[] }>(GET_DRAFT_BOOKS),
      ]);
      setBooks(booksData.books);
      setDraftBooksCount(draftsData.draftBooks.length);
    } catch (error) {
      console.error('Failed to load books:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBooks();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm(t('books.deleteConfirm'))) return;

    try {
      const client = getGraphQLClient();
      await client.request(DELETE_BOOK, { id });
      setBooks((prev) => prev.filter((s) => s.id !== id));
    } catch (error) {
      console.error('Failed to delete book:', error);
    }
  };

  const handlePublish = async (id: string, published: boolean) => {
    try {
      const client = getGraphQLClient();
      const response = await client.request<{
        publishBook: { book: { id: string; published: boolean; publishedAt?: string } };
      }>(PUBLISH_BOOK, { id, published });
      const updatedBook = response.publishBook.book;
      setBooks((prev) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, published: updatedBook.published, publishedAt: updatedBook.publishedAt }
            : s
        )
      );
    } catch (error) {
      console.error('Failed to publish/unpublish book:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('books.title')}</h1>
        <div className="flex items-center gap-4">
          {tab === 'books' && draftBooksCount > 0 && (
            <Link
              to="/books/drafts"
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              {t('books.drafts')} ({draftBooksCount})
            </Link>
          )}
          {tab === 'books' ? (
            <Link to="/books/new">
              <Button>
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('books.create')}
              </Button>
            </Link>
          ) : (
            <Button onClick={() => setShowCreateTranslation(true)}>
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('books.addTranslation')}
            </Button>
          )}
        </div>
      </div>

      {/* Books vs. Translations tabs */}
      <div className="mb-8 flex gap-6 border-b border-gray-200 dark:border-gray-700">
        {(['books', 'translations'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {key === 'books' ? t('books.tabBooks') : t('books.tabTranslations')}
          </button>
        ))}
      </div>

      {tab === 'translations' && (
        <TranslationsTab onAdd={() => setShowCreateTranslation(true)} />
      )}

      <CreateTranslationModal
        isOpen={showCreateTranslation}
        onClose={() => setShowCreateTranslation(false)}
        books={books}
      />

      {tab === 'books' &&
        (books.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <svg
            className="w-24 h-24 mx-auto mb-6 text-gray-300 dark:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('books.empty')}
          </h2>
          <Link to="/books/new">
            <Button variant="accent" size="lg">
              {t('books.create')}
            </Button>
          </Link>
        </motion.div>
      ) : (
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
          className="flex flex-col gap-6"
        >
          {books.map((book) => {
            // Determine the link path based on book status
            let linkPath: string;
            if (book.published) {
              linkPath = `/books/${book.id}`;
            } else if (book.status === 'draft' || book.status === 'in_progress') {
              if (book.plotBeatsCount > 0) {
                linkPath = `/books/new/${book.id}/sections`;
              } else if (book.summary) {
                linkPath = `/books/new/${book.id}/chapters`;
              } else {
                linkPath = `/books/new/${book.id}/summary`;
              }
            } else {
              linkPath = `/books/${book.id}`;
            }

            return (
              <motion.div
                key={book.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className="card-hover flex flex-row group"
              >
                {/* Cover image */}
                <Link to={linkPath} className="flex-shrink-0 p-2 sm:p-3 md:p-4">
                  {book.coverImageUrl ? (
                    <img
                      src={book.coverImageUrl}
                      alt={book.fullTitle || book.title || 'Book cover'}
                      className="w-20 sm:w-32 md:w-40 lg:w-48 h-auto aspect-[2/3] object-cover rounded-lg shadow-md"
                    />
                  ) : (
                    <div className="w-20 sm:w-32 md:w-40 lg:w-48 aspect-[2/3] bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 flex items-center justify-center rounded-lg shadow-md">
                      <svg
                        className="w-8 sm:w-12 md:w-16 h-8 sm:h-12 md:h-16 text-primary-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    </div>
                  )}
                </Link>

                {/* Book details */}
                <div className="flex-1 p-3 sm:p-4 md:p-6 flex flex-col min-w-0">
                  {/* Header with badges and actions */}
                  <div className="flex items-start justify-between mb-2 sm:mb-3 md:mb-4">
                    <div className="flex gap-1 sm:gap-2 flex-wrap">
                      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                        {t(`bookTypes.${book.bookType?.toLowerCase()}`)}
                      </span>
                      <span
                        className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                          book.status === 'completed'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : book.status === 'in_progress'
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {t(`statuses.${book.status.toLowerCase()}`)}
                      </span>
                      {book.published && (
                        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <svg
                            className="w-2.5 sm:w-3 h-2.5 sm:h-3 inline-block mr-0.5 sm:mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="hidden sm:inline">{t('books.published')}</span>
                        </span>
                      )}
                    </div>

                    <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                      <button
                        onClick={() => handlePublish(book.id, !book.published)}
                        className={`p-1 sm:p-1.5 rounded-lg transition-colors ${
                          book.published
                            ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20'
                            : 'text-gray-400 hover:text-green-500 hover:bg-green-50 dark:hover:text-green-400 dark:hover:bg-green-900/20'
                        }`}
                        title={book.published ? t('books.unpublish') : t('books.publish')}
                      >
                        <svg
                          className="w-4 sm:w-5 h-4 sm:h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(book.id)}
                        className="p-1 sm:p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title={t('books.delete')}
                      >
                        <svg
                          className="w-4 sm:w-5 h-4 sm:h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Title and description */}
                  <Link to={linkPath} className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-1 sm:mb-2 md:mb-3 hover:text-primary-600 dark:hover:text-primary-400 transition-colors line-clamp-2">
                      {book.fullTitle || book.title || t('books.untitledBook')}
                    </h3>

                    {book.summary && (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 sm:mb-4 line-clamp-2 sm:line-clamp-3 md:line-clamp-4">
                        {book.summary}
                      </p>
                    )}
                  </Link>

                  {/* Stats */}
                  <div className="flex items-center gap-2 sm:gap-4 text-[10px] sm:text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-auto pt-2 sm:pt-4 border-t border-gray-100 dark:border-gray-700">
                    <span className="flex items-center gap-0.5 sm:gap-1">
                      <svg
                        className="w-3 sm:w-4 h-3 sm:h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {book.plotBeatsCount}{' '}
                      <span className="hidden sm:inline">{t('books.beats')}</span>
                    </span>
                    <span className="flex items-center gap-0.5 sm:gap-1">
                      <svg
                        className="w-3 sm:w-4 h-3 sm:h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16m-7 6h7"
                        />
                      </svg>
                      {book.wordCount.toLocaleString()}{' '}
                      <span className="hidden sm:inline">{t('books.words')}</span>
                    </span>
                    {book.viewsCount !== undefined && book.viewsCount > 0 && (
                      <span className="flex items-center gap-0.5 sm:gap-1">
                        <svg
                          className="w-3 sm:w-4 h-3 sm:h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        {book.viewsCount.toLocaleString()}
                      </span>
                    )}
                    {book.favoritesCount !== undefined && book.favoritesCount > 0 && (
                      <span className="flex items-center gap-0.5 sm:gap-1">
                        <svg
                          className="w-3 sm:w-4 h-3 sm:h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                          />
                        </svg>
                        {book.favoritesCount.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
        ))}
    </div>
  );
}
